/* eslint-disable prettier/prettier */
import "../../styles/ComplaintsPage.scss";
import { Button, Input, Label } from "reactstrap";
import {
  ComplaintFileUploadPayload,
  ComplaintServiceSearchResult
} from "./types";
import { FirebaseReducer } from "react-redux-firebase";
import { compose } from "redux";
import { connect } from "react-redux";
import { functions } from "../../config/firebase";
import LoadingPage from "../loading/LoadingPage";
import React from "react";

interface ComplaintCreatePageProps {
  auth: FirebaseReducer.AuthState;
  profile: any;
}

const MOTIVE_OPTIONS = [
  "Defect of glass",
  "Defect of service",
  "Another situation..."
];

const toDisplayDate = (value: any): string => {
  if (!value) {
    return "-";
  }
  try {
    const date =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleDateString();
  } catch (error) {
    return "-";
  }
};

const toUploadPayload = (file: File): Promise<ComplaintFileUploadPayload> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const extension = file.name.includes(".")
        ? file.name.split(".").pop() || "dat"
        : "dat";
      resolve({
        file: result,
        ext: extension,
        name: file.name
      });
    };
    reader.onerror = () =>
      reject(reader.error || new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });
};

const ComplaintCreatePage: React.FC<any> = ({ auth, history, profile }) => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [searching, setSearching] = React.useState<boolean>(false);
  const [results, setResults] = React.useState<ComplaintServiceSearchResult[]>(
    []
  );
  const [selectedService, setSelectedService] =
    React.useState<ComplaintServiceSearchResult | null>(null);
  const [location, setLocation] = React.useState<string>("");
  const [motive, setMotive] = React.useState<string>(MOTIVE_OPTIONS[0]);
  const [description, setDescription] = React.useState<string>("");
  const [mediaFiles, setMediaFiles] = React.useState<File[]>([]);
  const [guaranteeFiles, setGuaranteeFiles] = React.useState<File[]>([]);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");
  const [success, setSuccess] = React.useState<string>("");

  const onSearch = async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setError("Enter a claim number or lifetime guarantee code to search.");
      setResults([]);
      return;
    }

    setSearching(true);
    setError("");
    setSuccess("");

    try {
      const callable = functions.httpsCallable("searchComplaintServices");
      const response = await callable({
        query: trimmed,
        partnerId: profile?.partnerId || null
      });
      const nextResults = Array.isArray(response?.data)
        ? (response.data as ComplaintServiceSearchResult[])
        : [];
      setResults(nextResults);
      if (!nextResults.length) {
        setSelectedService(null);
        setError(
          "No matching service was found. Try the exact claim number, registration, or guarantee code."
        );
      } else {
        setSuccess(
          `${nextResults.length} matching service${
            nextResults.length === 1 ? "" : "s"
          } found.`
        );
      }
    } catch (searchError) {
      console.error(searchError);
      setError("Could not search service records right now.");
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const onSelectService = (service: ComplaintServiceSearchResult) => {
    setSelectedService(service);
    setLocation(service.location || "");
    setError("");
    setSuccess("");
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedService) {
      setError("Select a service first before opening the complaint.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const [mediaPayloads, guaranteePayloads] = await Promise.all([
        Promise.all(mediaFiles.map((file) => toUploadPayload(file))),
        Promise.all(guaranteeFiles.map((file) => toUploadPayload(file)))
      ]);

      const callable = functions.httpsCallable("createComplaint");
      await callable({
        serviceClaimId: selectedService.claimId,
        partnerId: profile?.partnerId || null,
        location,
        motive,
        description,
        mediaFiles: mediaPayloads,
        guaranteeFiles: guaranteePayloads
      });

      setSuccess("Complaint created successfully.");
      history.push("/complaints");
    } catch (submitError) {
      console.error(submitError);
      setError("The complaint could not be created. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!auth.isLoaded) {
    return <LoadingPage />;
  }

  const creatorName = auth.displayName || auth.email || "Logged-in member";

  return (
    <div className="padded-container complaints-page-container">
      <div className="complaints-header">
        <div>
          <h1>Complaint Creation</h1>
          <span className="title-underline-v2" />
          <p>
            Search the service first, then open and submit the complaint.
          </p>
        </div>
        <Button
          className="back-compaints-btn"
          color="secondary"
          outline
          onClick={() => history.push("/complaints")}
        >
          Back to Complaints
        </Button>
      </div>

      <div className="complaint-create-grid">
        <section className="complaint-card complaint-search-card">
          <div className="section-title-row">
            <h2>1. Service Search</h2>
          </div>
          <div className="complaint-search-row">
            <Input
              value={searchTerm}
              placeholder="Claim Number or Global Lifetime Guarantee Code"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(event.target.value)
              }
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <Button color="primary" onClick={onSearch} disabled={searching}>
              {searching ? "Searching..." : "Search"}
            </Button>
          </div>

          {error ? <div className="complaint-feedback error">{error}</div> : null}
          {success ? (
            <div className="complaint-feedback success">{success}</div>
          ) : null}

          {results.length ? (
            <div className="complaint-search-results">
              {results.map((result) => (
                <button
                  type="button"
                  key={result.claimId}
                  className={`complaint-result-card ${
                    selectedService?.claimId === result.claimId ? "active" : ""
                  }`}
                  onClick={() => onSelectService(result)}
                >
                  <div>
                    <strong>{result.claimNumber || result.claimId}</strong>
                    <span>
                      {result.providerName ||
                        result.providerPartnerId ||
                        "Unknown provider"}
                    </span>
                  </div>
                  <div className="complaint-result-meta">
                    <span>
                      Service Date: {toDisplayDate(result.serviceDate)}
                    </span>
                    <span>Client: {result.clientName || "-"}</span>
                    <span>Location: {result.location || "-"}</span>
                  </div>
                  <span className="complaint-open-link">Open Complaint</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="empty-complaint-state">
              Search for a completed service to begin.
            </div>
          )}
        </section>

        <section className="complaint-card complaint-form-card">
          <div className="section-title-row">
            <h2>2. Complaint Form</h2>
          </div>

          {!selectedService ? (
            <div className="empty-complaint-state">
              Select a service result to unlock the complaint form.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="complaint-form">
              <div className="complaint-summary-grid">
                <div>
                  <Label>Member creating the complaint</Label>
                  <Input value={creatorName} readOnly />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={location}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setLocation(event.target.value)
                    }
                  />
                </div>
                <div>
                  <Label>Motive for complaint</Label>
                  <Input
                    type="select"
                    value={motive}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setMotive(event.target.value)
                    }
                  >
                    {MOTIVE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Input>
                </div>
                <div>
                  <Label>Service reference</Label>
                  <Input
                    value={
                      selectedService.claimNumber || selectedService.claimId
                    }
                    readOnly
                  />
                </div>
              </div>

              <div>
                <Label>Complaint details</Label>
                <Input
                  type="textarea"
                  rows={5}
                  value={description}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setDescription(event.target.value)
                  }
                  placeholder="Describe the issue, what happened, and the expected outcome."
                />
              </div>

              <div className="complaint-upload-grid">
                <div>
                  <Label>Media upload</Label>
                  <Input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const nextFiles = Array.from(
                        event.target.files || []
                      ) as File[];
                      setMediaFiles(nextFiles);
                    }}
                  />
                  <small>
                    {mediaFiles.map((file) => file.name).join(", ") ||
                      "Add photos and/or videos."}
                  </small>
                </div>
                <div>
                  <Label>Global lifetime guarantee PDF</Label>
                  <Input
                    type="file"
                    multiple
                    accept="application/pdf"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const nextFiles = Array.from(
                        event.target.files || []
                      ) as File[];
                      setGuaranteeFiles(nextFiles);
                    }}
                  />
                  <small>
                    {guaranteeFiles.map((file) => file.name).join(", ") ||
                      "Attach the guarantee PDF if available."}
                  </small>
                </div>
              </div>

              {error ? (
                <div className="complaint-feedback error">{error}</div>
              ) : null}
              {success ? (
                <div className="complaint-feedback success">{success}</div>
              ) : null}

              <div className="complaint-form-actions">
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Complaint"}
                </Button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

const mapStateToProps = ({
  firebase: { auth, profile }
}: {
  firebase: FirebaseReducer.Reducer;
}) => ({
  auth,
  profile
});

export default compose(connect(mapStateToProps))(ComplaintCreatePage);
